import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-200">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-none shadow-xl">
            <CardContent className="p-12">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6">Introduction</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Omnia Paratus Talent Solutions ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                  you visit our website or use our services.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Information We Collect</h2>
                <h3 className="text-2xl font-semibold text-[#0A1F44] mb-4 mt-8">Personal Information</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Submit a job application</li>
                  <li>Contact us through our website</li>
                  <li>Request information about our services</li>
                  <li>Subscribe to our communications</li>
                </ul>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  This information may include your name, email address, phone number, resume, work history, 
                  and other information relevant to employment or recruitment services.
                </p>

                <h3 className="text-2xl font-semibold text-[#0A1F44] mb-4 mt-8">Automatically Collected Information</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  When you visit our website, we may automatically collect certain information about your device, 
                  including information about your web browser, IP address, time zone, and some of the cookies 
                  installed on your device.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">How We Use Your Information</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Provide, operate, and maintain our recruitment services</li>
                  <li>Match candidates with appropriate job opportunities</li>
                  <li>Communicate with you about our services</li>
                  <li>Process your job applications and inquiries</li>
                  <li>Improve and optimize our website and services</li>
                  <li>Comply with legal obligations</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Information Sharing</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We may share your information with third parties in the following situations:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li><strong>With Employers:</strong> If you apply for a position, we may share your information 
                  with the prospective employer</li>
                  <li><strong>With Service Providers:</strong> We may share your information with third-party 
                  service providers who perform services on our behalf</li>
                  <li><strong>For Legal Purposes:</strong> We may disclose your information if required by law 
                  or in response to valid requests by public authorities</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Data Security</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal 
                  information. However, no method of transmission over the Internet or electronic storage is 100% 
                  secure, and we cannot guarantee absolute security.
                </p>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Your Rights</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Depending on your location, you may have certain rights regarding your personal information, including:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>The right to access your personal information</li>
                  <li>The right to request correction of inaccurate information</li>
                  <li>The right to request deletion of your information</li>
                  <li>The right to object to or restrict processing</li>
                  <li>The right to data portability</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Contact Us</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  If you have questions or concerns about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <p className="text-gray-700 mb-2">
                    <strong>Email:</strong> contact@omniaparatustalent.io
                  </p>
                  <p className="text-gray-700 mb-2">
                    <strong>Phone:</strong> (978) 340-6952
                  </p>
                  <p className="text-gray-700">
                    <strong>Location:</strong> Boston, MA
                  </p>
                </div>

                <h2 className="text-3xl font-bold text-[#0A1F44] mb-6 mt-12">Changes to This Policy</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by 
                  posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
